/**
 * Copyright (c) Omnissa, LLC. All rights reserved.
 * This product is protected by copyright and intellectual property laws in the
 * United States and other countries as well as by international treaties.
 * -- Omnissa Restricted
 *
 * @format
 */
/**
 * @fileoverview mediaEncoder.js -- MediaEncoder
 */
import { Injectable } from "@angular/core";
import { Logger } from "@html-core";
import { trustedTypeUtil } from "../../../jscdk/controllers/redirect/trusted-type-util";
import { CodecTypes, StreamHeaderLengthV2 } from "../rtav.constants";
import {
   AudioVideoSample,
   Callback,
   Header,
   HeaderCallbackData,
   LimitedSet,
   VideoCallbackData,
   arrayCopy
} from "./encoder.model";
import { VideoParams } from "./video-capture.model";

@Injectable({ providedIn: "root" })
export class VideoEncoderWorkerFactory {
   constructor() {}
   public newVideoEncoderWorker(
      deviceIndex: number,
      headerCallback: Callback<HeaderCallbackData>,
      dataCallback: Callback<VideoCallbackData>,
      param: VideoParams
   ) {
      return new VideoEncoderWorker(deviceIndex, headerCallback, dataCallback, param);
   }
}

export class VideoEncoderWorker {
   private worker: Worker;
   private header: Header;
   private codecPref: CodecTypes;
   private resourceCount: number;
   private encodingEnv: LimitedSet;
   private dataCount = 0;

   // throwable
   constructor(
      private deviceIndex: number,
      private headerCallback: Callback<HeaderCallbackData>,
      private dataCallback: Callback<VideoCallbackData>,
      param: VideoParams
   ) {
      this.resetEncoder();
      this.worker = new Worker(
         trustedTypeUtil.createTrustedScriptURL("./videoworker." + __BUILD_NUMBER__ + ".js") as string
      );
      param.logLevel = Logger.getLogLevel();
      this.codecPref = param.codecPref;
      this.worker.onmessage = this.handleMessage.bind(this);
      this.worker.postMessage({
         type: "Init",
         data: param,
         headerSize: StreamHeaderLengthV2
      });
      this.worker.onerror = (err) => {
         Logger.error("VideoWorker error: " + err.toString(), Logger.RTAV);
      };
   }

   public encode(sample: AudioVideoSample) {
      if (!this.worker || !sample || !sample.data) {
         return;
      }
      if (this.resourceCount > 0) {
         this.resourceCount--;
         const dataBuffer = sample.data;
         const envId = this.encodingEnv.insert({
            metaData: sample.timestamp,
            timestamp: sample.timestamp,
            others: sample.others
         });
         if (envId < 0) {
            Logger.error("VideoEncoder save environment object failed", Logger.RTAV);
            return;
         }
         this.worker.postMessage(
            {
               type: "Encode",
               data: dataBuffer,
               envId: envId
            },
            [dataBuffer]
         );
      }
   }

   public clear() {
      this.worker?.postMessage({
         type: "Clear"
      });
      this.worker?.terminate();
      this.resetEncoder();
   }

   private resetEncoder() {
      this.header = null;
      /**
       * number used for PV operation avoid taking too much of resources in the event queue,
       * and the values are get by testing on different machines, the bigger the number
       * the less data is dropped, but the more delay might exist.
       * in the release phase, if this object contains like 1, 1, we can change them into boolean,
       * but better to keep it for now without testing with the finalized codes.
       * @type {object}
       */
      this.resourceCount = 1;
      this.encodingEnv = new LimitedSet(this.resourceCount);
   }

   private handleMessage(evt: MessageEvent) {
      const message = evt.data;
      switch (message.type) {
         case "InitFail":
            Logger.debug("VideoWorker init failed", Logger.RTAV);
         case "InitDone":
            this.header = message.data;
            const mergedHeader = this.mergeHeader();
            if (this.codecPref === CodecTypes.CodecVmwH264 || this.codecPref === CodecTypes.CodecVmwH264Opus) {
               Logger.debug("set merge header", Logger.RTAV);
               this.headerCallback({
                  deviceIndex: this.deviceIndex,
                  header: mergedHeader
               });
            } else {
               Logger.error("VideoEncoder codec not supported", Logger.RTAV);
            }
            break;
         case "Encoded":
            const env = this.encodingEnv.fetch(message.envId);
            if (!env || !env.timestamp) {
               Logger.debug(
                  "VideoEncoder bad env read, might caused by switching devices to other desktops",
                  Logger.RTAV
               );
               return;
            }
            // the data might be null when the page is not fully loaded
            // in order to keep the data sending, we still process it even if it's null
            if (env.others) {
               const buffer = (message?.data?.buffer as ArrayBuffer) ?? null;
               const data = {
                  timestamp: env.timestamp,
                  others: env.others,
                  deviceIndex: this.deviceIndex,
                  dataCount: this.getDataCount(),
                  data: buffer
               };
               this.dataCallback(data);
            }
            this.resourceCount++;
            break;
         case "Cleared":
            Logger.debug("VideoEncoder cleared: " + message.success, Logger.RTAV);
            // TODO
            break;
      }
   }

   private mergeHeader() {
      const mergedHeaderBuffer = new ArrayBuffer(this.header.length + StreamHeaderLengthV2);
      arrayCopy(this.header.buffer, 0, mergedHeaderBuffer, StreamHeaderLengthV2, this.header.length);
      const mergedHeader: Header = {
         buffer: mergedHeaderBuffer,
         length: this.header.length
      };
      return mergedHeader;
   }

   private getDataCount() {
      this.dataCount++;
      return this.dataCount;
   }
}
